import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2,
  AlertOctagon,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { exceptionsAPI } from '../lib/v2Api';
import { formatDistanceToNow, format } from 'date-fns';

const SEVERITY_CONFIG = {
  p0: { label: 'P0 - Critical', color: 'text-red-700', bg: 'bg-red-100', badge: 'destructive' },
  p1: { label: 'P1 - High', color: 'text-amber-700', bg: 'bg-amber-100', badge: 'warning' },
  p2: { label: 'P2 - Medium', color: 'text-blue-700', bg: 'bg-blue-100', badge: 'secondary' },
};

const TYPE_LABELS = {
  negative_inventory: 'Negative Inventory',
  usage_exceeds_stock: 'Usage Exceeds Stock',
  high_variance: 'High Variance',
  unmapped_entity: 'Unmapped Entity',
  sync_mismatch: 'Sync Mismatch',
};

export const Exceptions = () => {
  const [loading, setLoading] = useState(true);
  const [exceptions, setExceptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  
  // Detail dialog
  const [selectedExc, setSelectedExc] = useState(null);
  
  // Resolve dialog
  const [resolveExc, setResolveExc] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveAction, setResolveAction] = useState('resolve'); // 'resolve' or 'ignore'

  useEffect(() => {
    loadData();
  }, [statusFilter, severityFilter, typeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { status: statusFilter, limit: 100 };
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (typeFilter !== 'all') params.exception_type = typeFilter;
      
      const [excRes, statsRes] = await Promise.all([
        exceptionsAPI.list(params),
        exceptionsAPI.stats(),
      ]);
      setExceptions(excRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load exceptions:', error);
      toast.error('Failed to load exceptions');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (exc) => {
    try {
      setActionLoading(exc.id);
      await exceptionsAPI.acknowledge(exc.id);
      toast.success('Exception acknowledged');
      loadData();
    } catch (error) {
      toast.error('Failed to acknowledge exception');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolveSubmit = async () => {
    if (!resolveExc) return;
    try {
      setActionLoading(resolveExc.id);
      if (resolveAction === 'resolve') {
        await exceptionsAPI.resolve(resolveExc.id, resolveNote || null);
        toast.success('Exception resolved');
      } else {
        await exceptionsAPI.ignore(resolveExc.id, resolveNote || null);
        toast.success('Exception ignored');
      }
      setResolveExc(null);
      setResolveNote('');
      loadData();
    } catch (error) {
      toast.error(`Failed to ${resolveAction} exception`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertOctagon className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Exceptions</h1>
              <p className="text-sm text-muted-foreground">
                Inventory discrepancies and reconciliation issues
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-2xl font-bold">{stats.total_open}</p>
                <p className="text-xs text-muted-foreground">Open</p>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-red-600">{stats.p0_count}</p>
                <p className="text-xs text-muted-foreground">P0 Critical</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-amber-600">{stats.p1_count}</p>
                <p className="text-xs text-muted-foreground">P1 High</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-blue-600">{stats.p2_count}</p>
                <p className="text-xs text-muted-foreground">P2 Medium</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-muted-foreground">{stats.acknowledged_count}</p>
                <p className="text-xs text-muted-foreground">Acknowledged</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters & Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-lg">Exception List</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <SearchableSelect
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'acknowledged', label: 'Acknowledged' },
                    { value: 'resolved', label: 'Resolved' },
                    { value: 'ignored', label: 'Ignored' },
                    { value: 'all', label: 'All' }
                  ]}
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                  placeholder="Status"
                  className="w-[120px]"
                  sortAlphabetically={false}
                />
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Severity' },
                    { value: 'p0', label: 'P0 Critical' },
                    { value: 'p1', label: 'P1 High' },
                    { value: 'p2', label: 'P2 Medium' }
                  ]}
                  value={severityFilter}
                  onValueChange={setSeverityFilter}
                  placeholder="Severity"
                  className="w-[120px]"
                  sortAlphabetically={false}
                />
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'negative_inventory', label: 'Negative Inventory' },
                    { value: 'usage_exceeds_stock', label: 'Usage Exceeds Stock' },
                    { value: 'high_variance', label: 'High Variance' },
                    { value: 'unmapped_entity', label: 'Unmapped Entity' },
                    { value: 'sync_mismatch', label: 'Sync Mismatch' }
                  ]}
                  value={typeFilter}
                  onValueChange={setTypeFilter}
                  placeholder="Type"
                  className="w-[140px]"
                  sortAlphabetically={false}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : exceptions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="font-medium text-lg">No Exceptions</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  No {statusFilter === 'all' ? '' : statusFilter} exceptions to review
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Severity</TableHead>
                      <TableHead>Issue</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Variance</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[150px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exceptions.map((exc) => {
                      const config = SEVERITY_CONFIG[exc.severity] || SEVERITY_CONFIG.p2;
                      
                      return (
                        <TableRow key={exc.id}>
                          <TableCell>
                            <Badge variant={config.badge}>
                              {exc.severity.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{exc.title}</p>
                              {exc.item_name && (
                                <p className="text-xs text-muted-foreground">{exc.item_name}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{TYPE_LABELS[exc.exception_type] || exc.exception_type}</span>
                          </TableCell>
                          <TableCell>
                            {exc.expected_value && exc.actual_value ? (
                              <div className="text-sm">
                                <span className="text-muted-foreground">{exc.expected_value}</span>
                                <ArrowRight className="w-3 h-3 inline mx-1" />
                                <span className="font-medium">{exc.actual_value}</span>
                              </div>
                            ) : exc.variance_amount ? (
                              <span className="font-mono">${exc.variance_amount.toFixed(2)}</span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {exc.created_at ? formatDistanceToNow(new Date(exc.created_at), { addSuffix: true }) : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setSelectedExc(exc)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {exc.status === 'open' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleAcknowledge(exc)}
                                  disabled={actionLoading === exc.id}
                                >
                                  {actionLoading === exc.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Clock className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {(exc.status === 'open' || exc.status === 'acknowledged') && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => { setResolveExc(exc); setResolveAction('resolve'); }}
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedExc} onOpenChange={() => setSelectedExc(null)}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Badge variant={SEVERITY_CONFIG[selectedExc?.severity]?.badge}>
                {selectedExc?.severity?.toUpperCase()}
              </Badge>
              <DialogTitle>{selectedExc?.title}</DialogTitle>
            </div>
          </DialogHeader>
          
          {selectedExc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium">{TYPE_LABELS[selectedExc.exception_type]}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">{selectedExc.status}</p>
                </div>
                {selectedExc.item_name && (
                  <div>
                    <p className="text-muted-foreground">Item</p>
                    <p className="font-medium">{selectedExc.item_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {selectedExc.created_at ? format(new Date(selectedExc.created_at), 'PPp') : '—'}
                  </p>
                </div>
              </div>
              
              {selectedExc.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm mt-1">{selectedExc.description}</p>
                </div>
              )}
              
              {(selectedExc.expected_value || selectedExc.actual_value) && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">What Changed</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{selectedExc.expected_value || '?'}</span>
                    <ArrowRight className="w-4 h-4" />
                    <span className="font-mono font-bold">{selectedExc.actual_value || '?'}</span>
                  </div>
                </div>
              )}
              
              {selectedExc.related_event_id && (
                <div className="text-xs text-muted-foreground">
                  Related Event: {selectedExc.related_event_id}
                </div>
              )}
              
              {selectedExc.resolution_note && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-700 mb-1">Resolution Note</p>
                  <p className="text-sm">{selectedExc.resolution_note}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedExc(null)}>Close</Button>
            {selectedExc?.status === 'open' && (
              <Button onClick={() => { setResolveExc(selectedExc); setSelectedExc(null); setResolveAction('resolve'); }}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolve
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={!!resolveExc} onOpenChange={() => { setResolveExc(null); setResolveNote(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolveAction === 'resolve' ? 'Resolve Exception' : 'Ignore Exception'}
            </DialogTitle>
            <DialogDescription>
              {resolveExc?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={resolveAction === 'resolve' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setResolveAction('resolve')}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Resolve
              </Button>
              <Button
                variant={resolveAction === 'ignore' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setResolveAction('ignore')}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Ignore
              </Button>
            </div>
            
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder={resolveAction === 'resolve' ? 'How was this resolved?' : 'Why is this being ignored?'}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResolveExc(null); setResolveNote(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleResolveSubmit}
              disabled={actionLoading === resolveExc?.id}
              variant={resolveAction === 'ignore' ? 'destructive' : 'default'}
            >
              {actionLoading === resolveExc?.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {resolveAction === 'resolve' ? 'Mark Resolved' : 'Mark Ignored'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Exceptions;
